import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import { useContext, useEffect } from 'react';
import { RemediationContext } from '../NRRemediationContext';
import { REMEDIATION_CONSTANTS } from '../NRRemediation.constants';
import StepperIndicator from '../NRRemediationContent/StepperIndicator';
import useNRMutation from '../../../graphql/UseNRMutation';
import NRButtonProgress from '../../NRButtonProgress/NRButtonProgress';
import { useSnackbar } from 'notistack';
import Divider from '@mui/material/Divider';
import { GridContext } from '../../NRReactGrid/NRReactGridContext';
import NRLicenseIssueRemediationStepOne from './NRLicenseIssueRemediationStepOne';
import NRLicenseIssueRemediationStepTwo from './NRLicenseIssueRemediationStepTwo';
import { LICENSE_ISSUES, LICENSE_ISSUES_EXTERNAL_FILTERS, REMEDIATE_LICENSE_ISSUES } from '../../../graphql/queries/licenseIssues';
import { useParams } from 'react-router-dom';
import { LICENSE_ISSUES_CONSTANTS } from '../../NRLicenseIssues/NRLicenseIssues.constants';
import { FiltersContext } from '../../../pages/NRReportTabs/NRReportFilterContextWrapper';

const steps = ['Status', 'Details'];

function NRLicenseIssueRemediationContent({
  inDrawer = false,
  licenseIssues,
  onSubmitExtraActions = () => {},
  onResponseExtraActions = () => {},
  onCancelExtraActions = () => {}
}) {
  const { lastQueryLicenseIssuesVariables, setOpenRemediationModal } = useContext(RemediationContext);
  const { setDrawerContent, drawerContent } = useContext(FiltersContext);
  const { componentId } = useParams();
  const { setShouldClearSelection } = useContext(GridContext);
  const { enqueueSnackbar } = useSnackbar();
  const [remediateLicenseIssues, result, loading] = useNRMutation(REMEDIATE_LICENSE_ISSUES, 'remediateLicenseIssues', null, null, {
    refetchQueries: [
      { query: LICENSE_ISSUES, variables: { ...lastQueryLicenseIssuesVariables }, context: { version: 'v3' } },
      { query: LICENSE_ISSUES_EXTERNAL_FILTERS, variables: { args: { assetId: componentId } }, context: { version: 'v3' } }
    ],
    awaitRefetchQueries: true,
    onCompleted: (result, refetch) => {
      if (inDrawer) {
        const newDrawerContent = [...drawerContent];
        setDrawerContent(newDrawerContent);
      }
    }
  });

  const emptyForm = {
    status: licenseIssues?.length === 1 ? licenseIssues[0].status : REMEDIATION_CONSTANTS.STATUS.VALUES.UNREVIEWED,
    description: licenseIssues?.length === 1 ? licenseIssues[0].remediation?.detail : '',
    licenseIssues: licenseIssues?.map(issue => issue.issueId)
  };
  const methods = useForm({
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: emptyForm
  });
  const { handleSubmit, watch, setValue } = methods;
  const form = watch();

  const onSubmitHandler = values => {
    onSubmitExtraActions(form);
    remediateLicenseIssues({
      context: { version: 'v3' },
      variables: {
        args: {
          assetId: componentId,
          details: values.description,
          issueIds: licenseIssues?.map(licenseIssue => licenseIssue.issueId),
          status: values.status
        }
      }
    });
  };

  useEffect(() => {
    if (result?.err === null) {
      enqueueSnackbar(LICENSE_ISSUES_CONSTANTS.REMEDIATION.SUCCESS_MESSAGE.replace(`${0}`, licenseIssues.length), { variant: 'success' });
      setOpenRemediationModal(false);
      onResponseExtraActions(form);
      setShouldClearSelection(true);
    }
  }, [result]);

  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleCancel = () => {
    onCancelExtraActions();
  };

  return (
    <Box {...(inDrawer && { height: '100%' })}>
      <FormProvider {...methods}>
        <form {...(inDrawer && { style: { height: '100%' } })} noValidate autoComplete="off" onSubmit={handleSubmit(onSubmitHandler)}>
          <Box
            width={'100%'}
            sx={{
              ...(inDrawer && {
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              })
            }}
          >
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel icon={<StepperIndicator number={index} completed={activeStep > index} active={activeStep === index} />}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <React.Fragment>
              {activeStep === 0 && <NRLicenseIssueRemediationStepOne />}
              {activeStep === 1 && <NRLicenseIssueRemediationStepTwo />}
              {inDrawer && <Divider sx={{ marginTop: 'auto', marginLeft: '-24px', marginRight: '-24px' }} />}
              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Button variant={'outlined'} color={'secondary'} onClick={() => handleCancel()} sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                <Button variant={'outlined'} color={'secondary'} disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
                  Back
                </Button>
                {activeStep !== steps.length - 1 && (
                  <Button onClick={handleNext} variant={'contained'} color={'secondary'}>
                    Next
                  </Button>
                )}
                {activeStep === steps.length - 1 && (
                  <NRButtonProgress variant="contained" color="secondary" type={'submit'} disabled={loading} isLoading={loading} label={'Finish'} />
                )}
              </Box>
            </React.Fragment>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
}

export default NRLicenseIssueRemediationContent;
